import { flag } from '@/lib/feedback';
import {
  DOC_STATUS_LABELS,
  DOC_STATUS_TONE,
  WORKFLOW,
  type AttachmentItem,
  type DocStatus,
  type DocumentEventItem,
  type DocumentSummary,
  type WorkflowAction,
} from '@ruby/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CornerUpLeft, Download, Eye, Paperclip, RotateCcw, ShieldCheck, Trash2, Upload, X } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { celebrate, stamp } from '@/lib/celebrate';
import { toast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/input';
import { Badge, Spinner } from '@/components/ui/misc';
import { api, apiUpload, errorMessage, openFile } from '@/lib/api';
import { cn, formatDateTime } from '@/lib/utils';
import { AttachmentThumb, AttachmentViewer } from './attachment-viewer';

export function StatusBadge({ status }: { status: DocStatus }) {
  // Waiting on someone: a small live dot (opacity only, cheap in long lists).
  const waiting = status === 'PENDING' || status === 'SENT_BACK';
  return (
    <Badge tone={DOC_STATUS_TONE[status]}>
      {waiting && <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-current" />}
      {DOC_STATUS_LABELS[status]}
    </Badge>
  );
}

const ACTION_ICON: Record<WorkflowAction, ReactNode> = {
  submit: <Upload />,
  approve: <Check />,
  send_back: <CornerUpLeft />,
  post: <Check />,
  cancel: <X />,
  reverse: <RotateCcw />,
  audit: <ShieldCheck />,
};

/**
 * The workflow buttons for a document. Shows only the actions the server says
 * the current user may take; asks for a comment where one is required.
 */
/** Milestones get the celebration: a production job released, a production order / finished goods posted. */
const MILESTONE_TYPES = ['mfg.work_order', 'mfg.fg_receipt'];
const isMilestone = (d: DocumentSummary, action: WorkflowAction) => action === 'approve' && MILESTONE_TYPES.includes(d.docType);

/** `hide`: actions this screen does not offer (e.g. approving a voucher outside Approvals). */
export function WorkflowActions({ doc, onDone, size = 'md', hide }: { doc: DocumentSummary; onDone?: (d: DocumentSummary) => void; size?: 'sm' | 'md'; hide?: readonly WorkflowAction[] }) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<WorkflowAction | null>(null);
  const [dialog, setDialog] = useState<WorkflowAction | null>(null);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState<string | null>(null);

  const run = async (action: WorkflowAction, text = '') => {
    setPending(action);
    try {
      const updated = await api<DocumentSummary>(`/documents/${doc.id}/actions/${action}`, { method: 'POST', body: { version: doc.version, comment: text } });
      // The stamp's chord first (a toast sound right after it is skipped), then the toast saying what happened.
      if (isMilestone(updated, action)) celebrate();
      else if (action === 'approve' || action === 'post') stamp(action === 'approve' ? 'Approved' : 'Posted');
      toast.success(`${WORKFLOW[action].label}: ${updated.number ?? doc.docTypeLabel}`);
      setDialog(null);
      setComment('');
      await qc.invalidateQueries({ queryKey: ['documents'] });
      onDone?.(updated);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setPending(null);
    }
  };

  const actions = doc.actions.filter((a) => !hide?.includes(a));
  const steps = doc.approval?.current ? doc.approval : null;
  if (!actions.length && !steps) return null;
  const danger = (a: WorkflowAction) => a === 'cancel' || a === 'reverse' || a === 'send_back';
  const resubmit = doc.status === 'SENT_BACK';
  // A sent-back document goes back with remarks on what was corrected.
  const needsComment = (a: WorkflowAction) => WORKFLOW[a].commentRequired || (a === 'submit' && resubmit);
  const actionLabel = (a: WorkflowAction) => (a === 'submit' && resubmit ? 'Resubmit for approval' : a === 'cancel' ? `Cancel ${doc.docTypeLabel.toLowerCase()}` : WORKFLOW[a].label);
  const PROMPT: Partial<Record<WorkflowAction, [string, string]>> = {
    submit: ['What was corrected?', 'e.g. Bank changed to ADIB; supplier bill attached'],
    send_back: ['What needs correcting?', 'e.g. Wrong bank — should be ENBD; attach the supplier bill'],
    cancel: ['Why is it cancelled?', 'e.g. Duplicate of BRV-2026-10-1012'],
    reverse: ['Why is it reversed?', 'e.g. Posted to the wrong customer'],
  };
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {steps && (
          <span className={cn('inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[12px]', doc.overdue ? 'fx-risk border-danger/50 text-danger' : 'border-border text-fg-muted')} title={steps.levels.map((l) => `${l.done ? '✓' : '○'} ${l.name}`).join('\n')}>
            <span className="flex gap-1">{steps.levels.map((l, i) => <i key={i} className={cn('size-2 rounded-full', l.done ? 'bg-success' : l.name === steps.current ? 'bg-warning fx-glow' : 'bg-border-strong')} />)}</span>
            Waiting for <b className="text-fg">{steps.current}</b>{doc.waitingHours != null && <span className="text-fg-subtle">· {doc.waitingHours}h</span>}
          </span>
        )}
        {actions.map((a) => (
          <Button
            key={a}
            size={size}
            variant={a === 'approve' || a === 'submit' || a === 'post' ? 'primary' : danger(a) ? 'danger-outline' : 'secondary'}
            loading={pending === a}
            disabled={!!pending}
            onClick={() => (needsComment(a) ? (setDialog(a), setComment(''), setCommentError(null)) : void run(a))}
          >
            {ACTION_ICON[a]} {actionLabel(a)}
          </Button>
        ))}
      </div>
      <Dialog
        open={!!dialog}
        onOpenChange={(v) => !pending && !v && setDialog(null)}
        title={dialog ? actionLabel(dialog) : ''}
        description={`${doc.docTypeLabel} ${doc.number ?? ''}`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialog(null)} disabled={!!pending}>Cancel</Button>
            <Button
              variant={dialog && danger(dialog) ? 'danger' : 'primary'}
              loading={!!pending}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (comment.trim().length < 3) return flag(setCommentError, 'Write at least 3 characters');
                void run(dialog!, comment.trim());
              }}
            >
              {dialog ? actionLabel(dialog) : ''}
            </Button>
          </>
        }
      >
        <label htmlFor="wf-comment" className="text-[13px] font-medium">
          {(dialog && PROMPT[dialog]?.[0]) ?? 'Reason'} <span className="text-danger">*</span>
        </label>
        <Textarea
          id="wf-comment"
          className="mt-1.5"
          rows={3}
          value={comment}
          invalid={!!commentError}
          onChange={(e) => {
            setComment(e.target.value);
            if (commentError && e.target.value.trim().length >= 3) setCommentError(null);
          }}
          placeholder={(dialog && PROMPT[dialog]?.[1]) ?? 'Explain why'}
          autoFocus
        />
        {commentError && <p className="mt-1 text-xs text-danger" role="alert">{commentError}</p>}
      </Dialog>
    </>
  );
}

const EVENT_LABEL: Record<string, string> = {
  create: 'Created',
  submit: 'Submitted for approval',
  approve: 'Approved',
  approve_level: 'Approved (level)',
  send_back: 'Sent back for correction',
  vat_override: 'VAT changed from the purchase order',
  migrated: 'Migrated from the old system',
  post: 'Posted to the ledger',
  system_post: 'Posted automatically',
  cancel: 'Cancelled',
  reverse: 'Reversed',
  audit: 'Audited',
  reversal_of: 'Reversal posted',
  edit: 'Edited',
};

/** One history line in words: resubmissions, corrections and bulk approvals read as such. */
function eventText(e: DocumentEventItem): { label: string; note?: string } {
  const bulk = e.comment?.startsWith('Bulk (');
  if (e.action === 'submit' && e.fromStatus === 'SENT_BACK') return { label: 'Resubmitted after correction', note: 'Corrected' };
  if (e.action === 'edit') return { label: e.fromStatus === 'SENT_BACK' ? 'Corrected' : 'Edited', note: 'Changes' };
  if (e.action === 'approve' && e.toStatus === 'POSTED') return { label: bulk ? 'Approved & posted (bulk approval)' : 'Approved & posted', note: 'Reason' };
  if (e.action === 'approve' && bulk) return { label: 'Approved (bulk approval)', note: 'Reason' };
  if (e.action === 'send_back' || e.action === 'cancel' || e.action === 'reverse') return { label: EVENT_LABEL[e.action]!, note: 'Reason' };
  if (e.action === 'vat_override') return { label: EVENT_LABEL[e.action]!, note: 'Change' };
  return { label: EVENT_LABEL[e.action] ?? e.action };
}

export function DocumentHistory({ documentId }: { documentId: string }) {
  const { data } = useQuery({ queryKey: ['documents', documentId, 'history'], queryFn: () => api<DocumentEventItem[]>(`/documents/${documentId}/history`) });
  if (!data) return <Spinner />;
  return (
    <ol className="fx-stagger relative space-y-4 border-l border-border pl-5">
      {data.map((e) => (
        <li key={e.id} className="relative">
          <span className={cn('fx-pop absolute -left-[26px] top-1 size-3 rounded-full border-2 border-surface shadow-[0_0_0_3px_color-mix(in_oklab,currentColor_0%,transparent)]', e.action === 'cancel' || e.action === 'reverse' ? 'bg-danger' : e.action === 'send_back' ? 'bg-warning' : e.action === 'edit' ? 'bg-border-strong' : 'bg-accent')} />
          <p className="text-[13px] font-semibold">{eventText(e).label}</p>
          <p className="text-xs text-fg-subtle">{e.actorName ?? 'System'} · {formatDateTime(e.at)}</p>
          {e.comment && <p className="mt-1 rounded-lg bg-surface-2 px-2.5 py-1.5 text-[13px] text-fg-muted">{eventText(e).note && <b className="font-medium text-fg">{eventText(e).note}: </b>}{e.comment.replace(/^Bulk \(\d+\): /, '')}</p>}
          {!!e.lineChanges?.length && (
            <ul className="mt-1 space-y-0.5 rounded-lg bg-surface-2 px-2.5 py-1.5 text-[12px] text-fg-muted">
              {e.lineChanges.map((c) => <li key={c}>{c}</li>)}
            </ul>
          )}
        </li>
      ))}
    </ol>
  );
}

const sizeText = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);

/** Files on a document, or on a master record (`entity`: partner, item, employee). */
export function AttachmentsPanel({ documentId, entity, canUpload, canRemove }: { documentId?: string; entity?: { type: string; id: string }; canUpload: boolean; canRemove: boolean }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState<number | null>(null);
  const base = documentId ? `/documents/${documentId}` : `/entities/${entity!.type}/${entity!.id}`;
  const { data } = useQuery({ queryKey: ['attachments', base], queryFn: () => api<AttachmentItem[]>(`${base}/attachments`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['attachments', base] });

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) await apiUpload(`${base}/attachments`, f);
      toast.success(files.length === 1 ? 'File attached' : `${files.length} files attached`);
      await refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      {data?.length === 0 && <p className="text-[13px] text-fg-subtle">No attachments.</p>}
      {/* Each file opens in the viewer on the page (owner, 2026-11-03); download stays one click away. */}
      {data?.map((a, n) => (
        <div key={a.id} className="group fx-rise flex items-center gap-3 rounded-xl border border-border px-2.5 py-2 transition-[border-color,box-shadow] duration-200 hover:border-border-strong hover:shadow-(--shadow-card)">
          <button type="button" onClick={() => setViewing(n)} className="flex min-w-0 flex-1 items-center gap-3 text-left" title={`View ${a.fileName}`}>
            <AttachmentThumb a={a} className="size-11" />
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-medium break-all group-hover:text-primary">{a.fileName}</span>
              <span className="block text-xs text-fg-subtle">{sizeText(a.sizeBytes)} · {a.uploadedBy ?? '—'} · {formatDateTime(a.uploadedAt)}</span>
            </span>
          </button>
          <Button variant="ghost" size="icon-sm" aria-label={`View ${a.fileName}`} title="View" onClick={() => setViewing(n)}>
            <Eye />
          </Button>
          <Button variant="ghost" size="icon-sm" aria-label={`Download ${a.fileName}`} title="Download" onClick={() => void openFile(`/attachments/${a.id}`, a.fileName, false, true).catch((e) => toast.error(errorMessage(e)))}>
            <Download />
          </Button>
          {canRemove && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Remove ${a.fileName}`}
              onClick={() => void api(`/attachments/${a.id}`, { method: 'DELETE' }).then(refresh, (e) => toast.error(errorMessage(e)))}
            >
              <Trash2 />
            </Button>
          )}
        </div>
      ))}
      {viewing !== null && data?.[viewing] && <AttachmentViewer files={data} start={viewing} onClose={() => setViewing(null)} />}
      {canUpload && (
        <>
          <input ref={input} type="file" multiple hidden accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.xlsx,.xls,.docx,.doc,.csv" onChange={(e) => void upload(e.target.files)} />
          <Button variant="secondary" size="sm" loading={busy} onClick={() => input.current?.click()}>
            <Paperclip /> Attach files
          </Button>
        </>
      )}
    </div>
  );
}

/** Optional files picked on a new document before it has an id; `flush` uploads them once it is saved. */
export function usePendingFiles() {
  const [files, setFiles] = useState<File[]>([]);
  const flush = async (documentId: string) => {
    let failed = 0;
    for (const f of files) await apiUpload(`/documents/${documentId}/attachments`, f).catch(() => failed++);
    if (failed) toast.error(`${failed} file(s) could not be attached — add them again on the saved voucher`);
    setFiles([]);
  };
  return { files, setFiles, flush };
}

export function PendingFilesPicker({ files, setFiles }: { files: File[]; setFiles(f: File[]): void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="space-y-2">
      {files.length === 0 && <p className="text-[13px] text-fg-subtle">Optional — invoice, receipt, bank slip…</p>}
      {files.map((f, i) => (
        <div key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-xl border border-border px-3 py-1.5 text-[13px]">
          <Paperclip className="size-4 shrink-0 text-fg-muted" />
          <span className="min-w-0 flex-1 break-all">{f.name}</span>
          <span className="text-[11.5px] text-fg-subtle">{sizeText(f.size)}</span>
          <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-fg-muted hover:text-danger"><X className="size-4" /></button>
        </div>
      ))}
      <input ref={input} type="file" multiple hidden onChange={(e) => { setFiles([...files, ...Array.from(e.target.files ?? [])]); e.target.value = ''; }} />
      <Button size="sm" variant="secondary" onClick={() => input.current?.click()}><Upload /> Add file</Button>
    </div>
  );
}
